import {defineStore} from 'pinia';
import api from '@/api/medsenger_api.js';
import ConversationMessage from '@/models/conversation_message.js';
import {timestamp} from '@/helpers/helpers.js';
import {emitter} from '@/infrastructure/event_bus.js';
import {useAccountStore} from '@/stores/AccountStore.js';
import {useConversationsStore} from '@/stores/ConversationsStore.js';

export const useConversationMessagesStore = defineStore('conversationMessages', {
    state: () => ({
        chats: [],
        initialized: false,
        visibleMessagesCount: {},
        messagesPerPage: 50,
    }),

    actions: {
        async initializeStore() {
            if (this.initialized) return;
            this.initialized = true;
            emitter.on('socketEvent', this.processEvent);
        },

        get(conversationId) {
            return this.chats.find(c => c.id === conversationId)?.messages || [];
        },

        getVisible(conversationId) {
            const allMessages = this.get(conversationId);
            const visibleCount = this.visibleMessagesCount[conversationId] || this.messagesPerPage;
            return allMessages.slice(-visibleCount);
        },

        loadMore(conversationId) {
            const allMessages = this.get(conversationId);
            const currentCount = this.visibleMessagesCount[conversationId] || this.messagesPerPage;
            const newCount = Math.min(currentCount + this.messagesPerPage, allMessages.length);
            this.visibleMessagesCount[conversationId] = newCount;
            return newCount < allMessages.length;
        },

        resetVisibleCount(conversationId) {
            this.visibleMessagesCount[conversationId] = this.messagesPerPage;
        },

        showAllMessages(conversationId) {
            const allMessages = this.get(conversationId);
            this.visibleMessagesCount[conversationId] = allMessages.length;
        },

        async downloadChatMessages(conversationId) {
            const chat = this.chats.find(c => c.id === conversationId);
            if (chat) return await this.update(conversationId);

            let now = timestamp();
            const answer = await api.conversations.getMessages(conversationId);

            if (answer.timestamp) {
                now = answer.timestamp - 5;
            }

            const newChat = {
                id: conversationId,
                messages: (answer.messages || []).map(md => new ConversationMessage(md)).filter(md => md.isActual()),
                last_update: now,
            };

            this.chats.push(newChat);
            return newChat.messages;
        },

        async update(conversationId) {
            const chat = this.chats.find(c => c.id === conversationId);
            if (!chat) {
                return this.downloadChatMessages(conversationId);
            }

            let updateStart = timestamp() - 1;
            const answer = await api.conversations.getMessages(conversationId, chat.last_update);

            if (answer.timestamp) {
                updateStart = answer.timestamp - 5;
            }

            chat.last_update = updateStart;

            const newMessages = answer.messages || [];
            if (newMessages.length) {
                let hasNewIncomingMessage = false;
                const currentUserId = useAccountStore().profile?.id;

                newMessages.forEach(mdata => {
                    const existing = chat.messages.find(m => m.id === mdata.id);
                    if (!existing) {
                        const newMessage = new ConversationMessage(mdata);
                        chat.messages.push(newMessage);

                        if (newMessage.author_user_id !== currentUserId && newMessage.isActual()) {
                            hasNewIncomingMessage = true;
                        }
                    } else {
                        existing.init(mdata);
                    }
                });

                if (hasNewIncomingMessage) {
                    const notificationAudio = new Audio('/sounds/message.mp3');
                    notificationAudio.pause();
                    notificationAudio.currentTime = 0;
                    notificationAudio.play().catch((e) => {
                        console.warn('Не удалось воспроизвести звук:', e);
                    });
                }

                chat.messages = chat.messages.filter(m => m.isActual());
            }

            return chat.messages;
        },

        async processEvent(event) {
            if (!useAccountStore().isAuthorized()) return;

            const selectedConversation = useConversationsStore().selectedConversation;
            if (selectedConversation?.id) {
                const messageUpdate = event.type === 'newConversationMessage'
                    && event.payload?.data?.conversation_id === selectedConversation.id;

                if (messageUpdate) {
                    await this.update(selectedConversation.id);
                    useConversationsStore().selectedConversation?.watch();
                    emitter.emit('newMessageAdded');
                }
            }
        },

        async deleteMessageLocally(conversationId, messageId) {
            const chat = this.chats.find(c => c.id === conversationId);
            if (chat) {
                const message = chat.messages.find(m => m.id === messageId);
                if (message) {
                    message.is_deleted = true;
                    chat.messages = chat.messages.filter(m => m.isActual());
                }
            }
        },

        async reset() {
            this.chats = [];
            this.visibleMessagesCount = {};
        },
    }
});
